import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Users, Download, Upload, FileSpreadsheet, CheckCircle2, XCircle, Trash2, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";

export default function ImportAnggota() {
  const { user } = useAuth();
  const [churches, setChurches] = useState([]);
  const [selectedChurch, setSelectedChurch] = useState("");
  const [anggota, setAnggota] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const canWrite = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","SEKRETARIS_JEMAAT","GEMBALA_JEMAAT"].includes(user?.role);

  useEffect(() => {
    api.get("/churches").then((r) => {
      const jm = r.data.filter((c) => c.type === "JEMAAT");
      setChurches(jm);
      // Auto-select user's church for jemaat roles
      if (user?.church_id) {
        setSelectedChurch(user.church_id);
      } else if (jm.length > 0) {
        setSelectedChurch(jm[0].id);
      }
      setLoading(false);
    });
  }, [user]);

  const loadAnggota = async () => {
    if (!selectedChurch) return;
    try {
      const { data } = await api.get(`/anggota?church_id=${selectedChurch}`);
      setAnggota(data);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { loadAnggota(); }, [selectedChurch]);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("simpel_token");
      const res = await fetch(`${API}/anggota/template`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) {
        toast.error("Gagal unduh template");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template-anggota-jemaat.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template diunduh");
    } catch (e) { toast.error("Gagal unduh template"); }
  };

  const doUpload = async () => {
    if (!file) { toast.error("Pilih file XLSX dulu"); return; }
    if (!selectedChurch) { toast.error("Pilih jemaat tujuan"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true); setResult(null);
    try {
      const { data } = await api.post(`/anggota/import?church_id=${selectedChurch}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.inserted} anggota berhasil ditambahkan.`);
      setFile(null);
      // Reset file input
      const input = document.querySelector('[data-testid="input-xlsx-file"]');
      if (input) input.value = "";
      loadAnggota();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus anggota ini?")) return;
    try {
      await api.delete(`/anggota/${id}`);
      toast.success("Anggota dihapus");
      loadAnggota();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = useMemo(() => {
    const kepala = anggota.filter(a => a.family_role === "KEPALA_KELUARGA").length;
    const laki = anggota.filter(a => a.gender === "L").length;
    const perempuan = anggota.filter(a => a.gender === "P").length;
    return { total: anggota.length, kepala, laki, perempuan };
  }, [anggota]);

  const currentChurch = churches.find(c => c.id === selectedChurch);

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Organisasi • Anggota Jemaat</div>
        <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
          <Users className="h-7 w-7 text-secondary" /> Import Anggota Jemaat
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Isi template Excel dengan data anggota (dasar + sakramental + pelayanan), lalu unggah untuk mengimport massal.
        </p>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Step 1: Download */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</span> Unduh Template Excel</CardTitle></CardHeader>
                <CardContent className="p-6 pt-0">
                  <div className="text-sm text-muted-foreground mb-3">
                    Template berisi 14 kolom: data dasar, sakramental (baptis/sidi/nikah), dan pelayanan (wadah/komisi).
                  </div>
                  <Button onClick={downloadTemplate} className="rounded-full" data-testid="btn-download-template">
                    <Download className="h-4 w-4 mr-2" /> Unduh Template XLSX
                  </Button>
                </CardContent>
              </Card>

              {/* Step 2: Upload */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">2</span> Unggah Template Terisi</CardTitle></CardHeader>
                <CardContent className="p-6 pt-0 space-y-3">
                  <div>
                    <Label>Jemaat Tujuan</Label>
                    <Select value={selectedChurch} onValueChange={setSelectedChurch} disabled={!!user?.church_id}>
                      <SelectTrigger data-testid="select-church-target"><SelectValue placeholder="Pilih jemaat" /></SelectTrigger>
                      <SelectContent>
                        {churches.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {user?.church_id && <div className="text-[0.65rem] text-muted-foreground mt-1">Dikunci ke jemaat Anda</div>}
                  </div>
                  <div>
                    <Label>File XLSX</Label>
                    <Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="input-xlsx-file" />
                    {file && <div className="text-xs text-muted-foreground mt-1">📎 {file.name} ({(file.size/1024).toFixed(1)} KB)</div>}
                  </div>
                  {canWrite && (
                    <Button onClick={doUpload} disabled={!file || uploading} className="rounded-full w-full" data-testid="btn-upload-xlsx">
                      <Upload className="h-4 w-4 mr-2" /> {uploading ? "Mengunggah…" : "Import Anggota"}
                    </Button>
                  )}
                </CardContent>
              </Card>

              {result && (
                <Card className="border-emerald-300 bg-emerald-50/50">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle2 className="h-6 w-6 text-emerald-700" />
                      <div>
                        <div className="font-heading font-bold text-lg text-emerald-900">Import berhasil</div>
                        <div className="text-xs text-emerald-800">
                          {result.inserted} baris ditambahkan, {result.skipped} dilewati
                          {result.errors?.length > 0 && `, ${result.errors.length} error`}
                        </div>
                      </div>
                    </div>
                    {result.errors?.length > 0 && (
                      <details className="text-xs text-rose-800 mt-2">
                        <summary className="cursor-pointer flex items-center gap-1"><XCircle className="h-3 w-3" /> Lihat error</summary>
                        <ul className="mt-1 space-y-0.5 list-disc list-inside">
                          {result.errors.map((e, i) => <li key={i}>Baris {e.row}: {e.error}</li>)}
                        </ul>
                      </details>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Data list */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Data Anggota {currentChurch ? `— ${currentChurch.short_name || currentChurch.name}` : ""}</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {anggota.length === 0 ? (
                    <div className="p-12 text-center">
                      <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                      <div className="text-sm text-muted-foreground">Belum ada anggota di jemaat ini.</div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-96 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="text-left p-2">No. Anggota</th>
                            <th className="text-left p-2">Nama</th>
                            <th className="text-left p-2">Gender</th>
                            <th className="text-left p-2">Lahir</th>
                            <th className="text-left p-2">HP</th>
                            <th className="text-left p-2">Peran Kel.</th>
                            <th className="text-left p-2">Baptis / Sidi</th>
                            <th className="text-left p-2">Wadah</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {anggota.map(a => (
                            <tr key={a.id} className="border-t border-border" data-testid={`row-anggota-${a.id}`}>
                              <td className="p-2 font-mono text-[0.7rem] text-muted-foreground whitespace-nowrap">{a.member_number || "—"}</td>
                              <td className="p-2 font-medium">{a.name}</td>
                              <td className="p-2">{a.gender || "—"}</td>
                              <td className="p-2 whitespace-nowrap">{a.birth_place ? `${a.birth_place}, ` : ""}{a.birth_date || "—"}</td>
                              <td className="p-2 whitespace-nowrap">{a.phone || "—"}</td>
                              <td className="p-2">{a.family_role ? a.family_role.replace(/_/g, " ") : "—"}</td>
                              <td className="p-2 text-[0.7rem]">
                                {a.baptis_date && <div>B: {a.baptis_date}</div>}
                                {a.sidi_date && <div>S: {a.sidi_date}</div>}
                              </td>
                              <td className="p-2">{a.wadah || "—"}</td>
                              <td className="p-2 text-right">
                                <div className="flex items-center gap-1 justify-end">
                                  <Link to={`/kartu-anggota/${a.id}`} className="text-primary hover:text-primary/70 p-1 rounded hover:bg-primary/10" data-testid={`btn-kartu-anggota-${a.id}`} title="Lihat Kartu">
                                    <CreditCard className="h-3.5 w-3.5" />
                                  </Link>
                                  {canWrite && (
                                    <button onClick={()=>del(a.id)} className="text-rose-600 hover:text-rose-800 p-1 rounded hover:bg-rose-50" data-testid={`btn-del-anggota-${a.id}`} title="Hapus"><Trash2 className="h-3.5 w-3.5" /></button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">
              <Card className="bg-primary/5 border-primary/30">
                <CardContent className="p-5">
                  <div className="text-[0.65rem] tracking-[0.18em] uppercase text-primary font-semibold">Ringkasan Jemaat</div>
                  <div className="font-heading font-bold text-4xl mt-1 text-primary">{grouped.total}</div>
                  <div className="text-xs text-muted-foreground">Total anggota terdaftar</div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div className="bg-white/60 rounded-md p-2">
                      <div className="text-[0.6rem] uppercase text-muted-foreground">KK</div>
                      <div className="font-heading font-bold text-lg">{grouped.kepala}</div>
                    </div>
                    <div className="bg-white/60 rounded-md p-2">
                      <div className="text-[0.6rem] uppercase text-muted-foreground">Pria</div>
                      <div className="font-heading font-bold text-lg">{grouped.laki}</div>
                    </div>
                    <div className="bg-white/60 rounded-md p-2">
                      <div className="text-[0.6rem] uppercase text-muted-foreground">Wanita</div>
                      <div className="font-heading font-bold text-lg">{grouped.perempuan}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5 text-xs space-y-2">
                  <div className="font-semibold text-foreground text-sm mb-2">Tips</div>
                  <div className="text-muted-foreground">• Format tanggal: YYYY-MM-DD (contoh: 1990-05-14)</div>
                  <div className="text-muted-foreground">• Kolom Nama Lengkap wajib. Sisanya opsional.</div>
                  <div className="text-muted-foreground">• Nomor HP disarankan +62… supaya bisa dipakai untuk WhatsApp reminder.</div>
                  <div className="text-muted-foreground">• Duplikasi tidak dicek — hati-hati sebelum unggah.</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}
